import { Volume2, Square, RotateCcw } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { useA11y, TEXT_SIZES, THEMES } from "@/context/A11yContext";

const TOGGLES = [
  { key: "dyslexic", label: "Dyslexia-friendly font" },
  { key: "spacing", label: "Extra line and letter spacing" },
  { key: "links", label: "Underline all links" },
  { key: "reduceMotion", label: "Reduce motion" },
];
const RATES = [
  { v: 0.8, label: "Slower" },
  { v: 1, label: "Normal" },
  { v: 1.2, label: "Faster" },
];

const Group = ({ title, children }) => (
  <fieldset className="space-y-2.5">
    <legend className="eyebrow mb-2.5">{title}</legend>
    {children}
  </fieldset>
);

export const A11yPanel = () => {
  const { settings, update, reset, speak, stop, speaking, canSpeak } = useA11y();
  const readPage = () => {
    if (speaking) return stop();
    const sel = window.getSelection()?.toString().trim();
    return speak(sel || document.querySelector("main")?.innerText || "");
  };
  return (
    <div className="space-y-6 text-ink" data-testid="a11y-panel">
      <Group title="Text size">
        <div className="grid grid-cols-4 gap-2">
          {TEXT_SIZES.map((s, i) => (
            <button key={s} type="button" aria-pressed={settings.size === i} onClick={() => update({ size: i })}
              data-testid={`a11y-size-${s}`}
              className={`rounded-xl border-2 py-2 font-bold transition-colors ${settings.size === i ? "border-brand bg-brand text-brand-fg" : "border-line hover:border-ink"}`}
              style={{ fontSize: `${12 + i * 3}px` }}>
              A<span className="sr-only"> {s}%</span>
            </button>
          ))}
        </div>
      </Group>
      <Group title="Background colour">
        <div className="grid grid-cols-3 gap-2">
          {THEMES.map((t) => (
            <button key={t.id} type="button" aria-pressed={settings.theme === t.id} onClick={() => update({ theme: t.id })}
              data-testid={`a11y-theme-${t.id}`}
              className={`flex flex-col items-center gap-1.5 rounded-xl border-2 p-2 text-xs font-bold transition-colors ${settings.theme === t.id ? "border-ink" : "border-line hover:border-ink-soft"}`}>
              <span className="flex h-8 w-full items-center justify-center rounded-lg text-sm font-bold" style={{ background: t.bg, color: t.fg, border: "1px solid rgba(0,0,0,.15)" }} aria-hidden="true">Aa</span>
              {t.label}
            </button>
          ))}
        </div>
      </Group>
      <Group title="Reading">
        {TOGGLES.map((t) => (
          <div key={t.key} className="flex items-center justify-between gap-3">
            <label htmlFor={`a11y-${t.key}`} className="text-sm font-bold">{t.label}</label>
            <Switch id={`a11y-${t.key}`} checked={settings[t.key]} onCheckedChange={(v) => update({ [t.key]: v })}
              data-testid={`a11y-toggle-${t.key}`}
              className="data-[state=checked]:bg-brand data-[state=unchecked]:bg-line" />
          </div>
        ))}
      </Group>
      {canSpeak && (
        <Group title="Read aloud">
          <div className="grid grid-cols-3 gap-2">
            {RATES.map((r) => (
              <button key={r.v} type="button" aria-pressed={settings.rate === r.v} onClick={() => update({ rate: r.v })}
                data-testid={`a11y-rate-${r.label.toLowerCase()}`}
                className={`rounded-xl border-2 py-1.5 text-xs font-bold ${settings.rate === r.v ? "border-brand bg-brand text-brand-fg" : "border-line"}`}>
                {r.label}
              </button>
            ))}
          </div>
          <button type="button" onClick={readPage} className="btn btn-brand w-full" data-testid="a11y-read-page">
            {speaking ? <Square size={16} aria-hidden /> : <Volume2 size={16} aria-hidden />}
            {speaking ? "Stop reading" : "Read page (or selected text)"}
          </button>
        </Group>
      )}
      <button type="button" onClick={reset} className="btn btn-ghost btn-sm w-full" data-testid="a11y-reset">
        <RotateCcw size={14} aria-hidden /> Reset accessibility settings
      </button>
    </div>
  );
};
